import React, { useEffect, useRef, useState } from 'react';
import Type from 'prop-types';
import { Checkbox, ListSubheader } from '@mui/material';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
} from 'react-virtualized';
import { useWindowSize } from 'react-use';
import { useTranslation } from 'hooks';
import Avatar from '../../Avatar';
import ActivityIndicator from '../../ActivityIndicator';
import EmptyState from '../../EmptyState';
import AppBar from '../../AppBar';
import SearchBar from '../../Form/SearchBar';
import styles from './ListPicker.module.css';

const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 75,
});

const ListPicker = (props) => {
  const {
    subheader,
    options: optionsProp,
    selectedOptions: selectedOptionsProp,
    showMe,
    showAvatar,
    loading,
    searchable,
    authUser,
    onQueryChange,
    onIconClick,
    isSelected,
    onSelect,
    title,
  } = props;
  const { height: pageHeight } = useWindowSize();
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation();
  const ref = useRef();
  const showSelectedOptions = searchQuery.length === 0 && searchable;

  useEffect(() => {
    return () => {
      // при переходе между списками контактов значение высоты строки необходимо очищать в кеше
      cache.clearAll();
    };
  }, []);

  useEffect(() => {
    cache.clearAll();
  }, [optionsProp]);

  const handleSearch = (value) => {
    const limit = Number.parseInt(value, 10) ? 0 : 2;
    setSearchQuery(value);
    if (value.length > limit) {
      onQueryChange(value);
    }
  };

  const handleOptionSelected = (option) => () => {
    onSelect(option);
  };

  const renderAppBar = () => {
    return <AppBar title={title} navIcon="back" onNavClick={onIconClick} />;
  };

  const renderSearchBar = () => {
    return (
      <SearchBar
        value={searchQuery}
        onSearch={handleSearch}
        onIconClick={onIconClick}
        position="static"
        showDivider
        fullWidth
      />
    );
  };

  const renderSubheader = () => {
    if (!subheader) {
      return null;
    }
    return (
      <ListSubheader className={styles.subheader}>{subheader}</ListSubheader>
    );
  };

  const renderItem = (item) => {
    return (
      <div className={styles.listPickerContainer}>
        <div
          role="presentation"
          key={item.id}
          id="saveChanges"
          className={styles.listPickerWrapper}
          onClick={handleOptionSelected(item)}
        >
          {showAvatar && (
            <div className={styles.avatarWrapper}>
              <Avatar src={item.avatarUrl} name={item.name} alt={item.name} />
            </div>
          )}
          <div className={styles.listPickerTextWrapper}>
            <span className={styles.listPickerText}>{item.name}</span>
            <div className={styles.listPickerId}>
              {item.clgroup !== undefined
                ? `(${item.id}) ${item.clgroup}`
                : item.id}
            </div>
          </div>
        </div>
        <div className={styles.listPickerCheckbox}>
          <Checkbox
            key={`checkbox-${item.id}`}
            color="primary"
            edge="end"
            onChange={handleOptionSelected(item)}
            checked={isSelected(item)}
            inputProps={{ 'aria-labelledby': item.id }}
          />
        </div>
      </div>
    );
  };

  const renderMe = () => {
    const item = {
      id: authUser.uname,
      name: `${authUser.firstName} ${authUser.secondName}`,
      avatarUrl: authUser.avatarUrl,
    };
    return renderItem(item);
  };

  const renderCellMeasurerItem = (item, rowData) => {
    const { index, parent, key, style } = rowData;
    return (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        <div style={style}>{renderItem(item)}</div>
      </CellMeasurer>
    );
  };

  const rowRendererSearchedItems = (rowData) => {
    const { index } = rowData;
    const item = optionsProp[index];
    if (showMe && item.id === authUser.uname) {
      return null;
    }
    return renderCellMeasurerItem(item, rowData);
  };

  const rowRendererSelectedItems = (rowData) => {
    const { index } = rowData;
    const selectedOptions = selectedOptionsProp || [];
    const item = selectedOptions[index];
    return renderCellMeasurerItem(item, rowData);
  };

  const renderSearchResults = (list) => {
    const options = list || [];
    const rowCount = options.length;

    if (loading) {
      return <ActivityIndicator />;
    }

    if (rowCount === 0) {
      if (showMe) return null;
      if (!showSelectedOptions) {
        return (
          <EmptyState
            title={t('noMatches')}
            subtitle={`${t('noResults')}. ${t('tryChangeSearchParams')}.`}
          />
        );
      }
      return <EmptyState title="" subtitle={t('requiredThreeCharacters')} />;
    }

    return (
      <AutoSizer disableHeight>
        {({ width }) => (
          <List
            ref={ref}
            height={pageHeight}
            overscanRowCount={10}
            rowCount={rowCount}
            deferredMeasurementCache={cache}
            rowHeight={cache.rowHeight}
            rowRenderer={
              showSelectedOptions
                ? rowRendererSelectedItems
                : rowRendererSearchedItems
            }
            width={width}
            disablePadding
          />
        )}
      </AutoSizer>
    );
  };

  const renderList = () => {
    return showSelectedOptions
      ? renderSearchResults(selectedOptionsProp)
      : renderSearchResults(optionsProp);
  };

  return (
    <>
      {searchable && renderSearchBar()}
      {!searchable && renderAppBar()}
      {renderSubheader()}
      {renderList()}
      {showMe && renderMe()}
    </>
  );
};

ListPicker.propTypes = {
  title: Type.string,
  authUser: Type.shape({
    uname: Type.string.isRequired,
    firstName: Type.string.isRequired,
    secondName: Type.string.isRequired,
    avatarUrl: Type.string,
  }),
  subheader: Type.node,
  options: Type.arrayOf(
    Type.shape({
      id: Type.string,
      name: Type.string,
    }),
  ).isRequired,
  selectedOptions: Type.arrayOf(
    Type.shape({
      id: Type.string,
      name: Type.string,
    }),
  ).isRequired,
  isSelected: Type.func.isRequired,
  loading: Type.bool,
  onQueryChange: Type.func.isRequired,
  onIconClick: Type.func.isRequired,
  onSelect: Type.func.isRequired,
  showMe: Type.bool,
  showAvatar: Type.bool,
  searchable: Type.bool,
};

ListPicker.defaultProps = {
  title: null,
  subheader: null,
  authUser: null,
  showMe: false,
  showAvatar: true,
  loading: false,
  searchable: true,
};

export default ListPicker;
